import { useState, useEffect, useMemo } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Clock, Plus, RefreshCw, Building, Edit, Trash2 } from 'lucide-react';
import { BlocoHorarioService } from '@/services/blocoHorarioService';
import { SalaService } from '@/services/salaService';
import GenerationModal from '@/components/admin/GenerationModal';
import ResponsiveAdminLayout from '@/components/admin/ResponsiveAdminLayout';

import { ResponsiveTable, ColumnDef } from '@/components/ui/responsive-table';
import { useTableColumns } from '@/hooks/useTableColumns';
import { ResponsiveForm, TouchInput } from '@/components/ui/responsive-form';
import { useResponsive } from '@/hooks/useResponsive';
import { CreateBlocoHorarioRequest } from '@/types/blocoHorario';

// Types
interface BlocoHorario {
  id: string;
  sala_id: string;
  hora_inicio: string;
  hora_fim: string;
  valor: number;
  ativo: boolean;
  sala?: {
    id: string;
    nome: string;
    descricao?: string;
    capacidade: number;
    equipamentos: string[];
    imagem_url?: string;
    is_active: boolean;
  };
}

interface Sala {
  id: string;
  nome: string;
  is_active: boolean;
}

interface Metrics {
  totalBlocos: number;
  blocosAtivos: number;
  valorMedio: number;
  totalSalas: number;
}

// Form schema
const blocoSchema = z.object({
  sala_id: z.string().min(1, 'Selecione uma sala'),
  hora_inicio: z.string().min(1, 'Hora início é obrigatória'),
  hora_fim: z.string().min(1, 'Hora fim é obrigatória'),
  valor: z.number().min(0.01, 'Valor deve ser maior que zero'),
  ativo: z.boolean().default(true)
});

type BlocoFormData = z.infer<typeof blocoSchema>;

const AdminTimeBlocks = () => {
  const [blocos, setBlocos] = useState<BlocoHorario[]>([]);
  const [salas, setSalas] = useState<Sala[]>([]);
  const [metrics, setMetrics] = useState<Metrics>({
    totalBlocos: 0,
    blocosAtivos: 0,
    valorMedio: 0,
    totalSalas: 0
  });
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBloco, setEditingBloco] = useState<BlocoHorario | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isMobile } = useResponsive();

  const { register, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm<BlocoFormData>({
    resolver: zodResolver(blocoSchema),
    defaultValues: {
      ativo: true
    }
  });

  // ✅ OTIMIZAÇÃO: Debounce para busca
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Carregar dados sem logs excessivos
      const [blocosResponse, salasResponse] = await Promise.all([
        BlocoHorarioService.getBlocosHorarios(1, 100),
        SalaService.getSalas(1, 100, undefined, true)
      ]);
      
      // Processar blocos
      let blocosData = [];
      if (blocosResponse) {
        if (Array.isArray(blocosResponse.blocos)) {
          blocosData = blocosResponse.blocos;
        } else if (Array.isArray(blocosResponse)) {
          blocosData = blocosResponse;
        }
      }
      
      // Processar salas
      let salasData = [];
      if (salasResponse) {
        if (Array.isArray(salasResponse.salas)) {
          salasData = salasResponse.salas;
        } else if (Array.isArray(salasResponse)) {
          salasData = salasResponse;
        }
      }

      const salasAtivas = salasData.filter(sala => sala?.is_active);
      
      setBlocos(blocosData);
      setSalas(salasAtivas);

      // Calculate metrics
      const totalBlocos = blocosData.length;
      const blocosAtivos = blocosData.filter(b => b.ativo).length;
      const valorMedio = blocosData.length > 0 
        ? blocosData.reduce((acc, b) => acc + b.valor, 0) / blocosData.length 
        : 0;
      const totalSalas = salasData.filter(sala => sala.is_active).length;

      setMetrics({
        totalBlocos,
        blocosAtivos,
        valorMedio,
        totalSalas
      });
      
      
    } catch (error: unknown) {
      console.error('Erro ao carregar dados:', error);
      // Reset states to safe values in case of error
      setBlocos([]);
      setSalas([]);
      setMetrics({
        totalBlocos: 0,
        blocosAtivos: 0,
        valorMedio: 0,
        totalSalas: 0
      });
      
      const message = error instanceof Error ? error.message : 'Erro desconhecido ao carregar dados';
      toast.error('Erro ao carregar dados: ' + message);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: BlocoFormData) => {
    try {
      setIsSubmitting(true);
      if (editingBloco) {
        await BlocoHorarioService.updateBlocoHorario(editingBloco.id, data);
        toast.success('Bloco horário atualizado com sucesso!');
      } else {
        await BlocoHorarioService.createBlocoHorario(data as CreateBlocoHorarioRequest);
        toast.success('Bloco horário criado com sucesso!');
      }
      await loadData();
      setIsDialogOpen(false);
      resetForm();
    } catch (error: unknown) {
      console.error('Erro ao salvar bloco horário:', error);
      const message = error instanceof Error ? error.message : 'Erro desconhecido ao salvar bloco horário';
      toast.error('Erro ao salvar bloco horário: ' + message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (bloco: BlocoHorario) => {
    setEditingBloco(bloco);
    reset({
      sala_id: bloco.sala_id,
      hora_inicio: bloco.hora_inicio,
      hora_fim: bloco.hora_fim,
      valor: bloco.valor,
      ativo: bloco.ativo
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await BlocoHorarioService.deleteBlocoHorario(id);
      toast.success('Bloco horário excluído com sucesso!');
      await loadData();
    } catch (error: unknown) {
      console.error('Erro ao excluir bloco horário:', error);
      const message = error instanceof Error ? error.message : 'Erro desconhecido ao excluir bloco horário';
      toast.error('Erro ao excluir bloco horário: ' + message);
    }
  };

  const handleToggleStatus = async (id: string) => {
    try {
      await BlocoHorarioService.toggleStatus(id);
      toast.success('Status do bloco horário atualizado!');
      await loadData();
    } catch (error: unknown) {
      console.error('Erro ao atualizar status:', error);
      const message = error instanceof Error ? error.message : 'Erro desconhecido ao atualizar status';
      toast.error('Erro ao atualizar status: ' + message);
    }
  };

  const resetForm = () => {
    reset({
      sala_id: '',
      hora_inicio: '',
      hora_fim: '',
      valor: 0,
      ativo: true
    });
    setEditingBloco(null);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Convert metrics object to ResponsiveMetricsGrid format
  const metricsCards = useMemo(() => [
    {
      title: "Total de Blocos",
      value: metrics.totalBlocos.toString(),
      change: "blocos cadastrados",
      bg: "bg-gradient-to-r from-blue-500 to-cyan-500",
      icon: Clock
    },
    {
      title: "Blocos Ativos",
      value: metrics.blocosAtivos.toString(),
      change: "disponíveis para reserva",
      bg: "bg-gradient-to-r from-green-500 to-emerald-500", 
      icon: Plus
    },
    {
      title: "Valor Médio",
      value: formatCurrency(metrics.valorMedio),
      change: "por bloco horário",
      bg: "bg-gradient-to-r from-purple-500 to-pink-500",
      icon: RefreshCw
    },
    {
      title: "Salas Ativas",
      value: metrics.totalSalas.toString(),
      change: "salas disponíveis",
      bg: "bg-gradient-to-r from-orange-500 to-red-500",
      icon: Building
    }
  ], [metrics]);

  // ✅ FIX: Filtragem segura para evitar crash com dados nulos
  const filteredBlocos = useMemo(() => {
    if (!blocos) return [];
    return blocos.filter(bloco => {
      const searchTermLower = debouncedSearchTerm.toLowerCase();
      const salaNome = bloco.sala?.nome?.toLowerCase() || '';
      const horaInicio = bloco.hora_inicio?.toLowerCase() || '';
      
      return salaNome.includes(searchTermLower) || horaInicio.includes(searchTermLower);
    });
  }, [blocos, debouncedSearchTerm]);

  const groupedBlocos = filteredBlocos.reduce((acc, bloco) => {
    const salaNome = bloco.sala?.nome || 'Sala sem nome';
    if (!acc[salaNome]) {
      acc[salaNome] = [];
    }
    acc[salaNome].push(bloco);
    return acc;
  }, {} as Record<string, BlocoHorario[]>);

  // Configuração das colunas da tabela responsiva
  const { createColumn } = useTableColumns<BlocoHorario>();
  
  const columns: ColumnDef<BlocoHorario>[] = [
    createColumn('horario', 'Horário', (bloco) => (
      <span className="font-medium">
        {(bloco.hora_inicio || '-')} - {(bloco.hora_fim || '-')}
      </span>
    )),
    createColumn('duracao', 'Duração', (bloco) => {
      if (!bloco.hora_inicio || !bloco.hora_fim) {
        return <span className="text-gray-600">-</span>;
      }
      const duration = BlocoHorarioService.calculateDuration(
        bloco.hora_inicio, 
        bloco.hora_fim
      );
      return (
        <span className="text-gray-600">
          {BlocoHorarioService.formatDuration(duration)}
        </span>
      );
    }),
    createColumn('valor', 'Valor', (bloco) => (
      <span className="font-medium">
        {formatCurrency(bloco.valor)}
      </span>
    )),
    createColumn('status', 'Status', (bloco) => (
      <Badge variant={bloco.ativo ? 'default' : 'secondary'}>
        {bloco.ativo ? 'Ativo' : 'Inativo'}
      </Badge>
    )),
    createColumn('actions', 'Ações', (bloco) => (
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleToggleStatus(bloco.id)}
        >
          {bloco.ativo ? 'Desativar' : 'Ativar'}
        </Button>
        
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleEdit(bloco)}
        >
          <Edit className="w-4 h-4" />
        </Button>
        
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" size="sm">
              <Trash2 className="w-4 h-4" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
              <AlertDialogDescription>
                Tem certeza que deseja excluir este bloco horário?
                Esta ação não pode ser desfeita.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleDelete(bloco.id)}
                className="bg-red-600 hover:bg-red-700"
              >
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    ))
  ];

  // Card mobile personalizado para blocos horários
  const renderBlocoMobileCard = (bloco: BlocoHorario, index: number) => {
    const duration = (bloco.hora_inicio && bloco.hora_fim) 
      ? BlocoHorarioService.calculateDuration(bloco.hora_inicio, bloco.hora_fim)
      : 0;
    
    return (
      <Card key={index} className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-lg">
            {(bloco.hora_inicio || '-')} - {(bloco.hora_fim || '-')}
          </h3>
          <Badge variant={bloco.ativo ? 'default' : 'secondary'}>
            {bloco.ativo ? 'Ativo' : 'Inativo'}
          </Badge>
        </div>
        
        <div className="space-y-2 text-sm mb-4">
          <div className="flex items-center justify-between">
            <span className="text-gray-500">Duração:</span>
            <span>{BlocoHorarioService.formatDuration(duration)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-500">Valor:</span>
            <span className="font-semibold text-green-600">
              {formatCurrency(bloco.valor)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-500">Sala:</span>
            <span>{bloco.sala?.nome || 'Sala sem nome'}</span>
          </div>
        </div>
        
        <div className="flex gap-2">
          <Button 
            size="sm" 
            variant="outline"
            onClick={() => handleToggleStatus(bloco.id)}
            className="flex-1"
          >
            {bloco.ativo ? 'Desativar' : 'Ativar'}
          </Button>
          <Button 
            size="sm" 
            variant="outline"
            onClick={() => handleEdit(bloco)}
          >
            <Edit className="w-4 h-4" />
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Trash2 className="w-4 h-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className={isMobile ? 'max-w-[95vw]' : ''}>
              <AlertDialogHeader>
                <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                <AlertDialogDescription>
                  Tem certeza que deseja excluir este bloco horário?
                  Esta ação não pode ser desfeita.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className={isMobile ? 'flex-col gap-2' : ''}>
                <AlertDialogCancel className={isMobile ? 'w-full' : ''}>
                  Cancelar
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => handleDelete(bloco.id)}
                  className={`bg-red-600 hover:bg-red-700 ${isMobile ? 'w-full' : ''}`}
                >
                  Excluir
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </Card>
    );
  };

  return (
    <ResponsiveAdminLayout
      title="Blocos Horários"
      subtitle="Configure os horários e valores das sessões"
    >
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Clock className="w-12 h-12 text-blue-500 mx-auto mb-4 animate-spin" />
            <p className="text-gray-600">Carregando blocos horários...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Search and Action Buttons */}
          <div className={`mb-6 flex ${isMobile ? 'flex-col gap-4' : 'items-center justify-between gap-4'}`}>
            <Input
              placeholder="Pesquisar blocos horários..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={isMobile ? 'w-full' : 'max-w-md'}
            />
            
            <div className={`flex items-center gap-2 ${isMobile ? 'justify-center' : ''}`}>
              <Dialog>
                <DialogTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="bg-red-600 hover:bg-red-700 text-white"
                    size={isMobile ? "sm" : "default"}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    {isMobile ? 'Gerar' : 'Gerar Blocos (365 dias)'}
                  </Button>
                </DialogTrigger>
                <GenerationModal onComplete={loadData} />
              </Dialog>
              
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={resetForm} size={isMobile ? "sm" : "default"} className="bg-blue-600 hover:bg-blue-700 text-white">
                    <Plus className="w-4 h-4 mr-2" />
                    {isMobile ? 'Novo' : 'Novo Bloco'}
                  </Button>
                </DialogTrigger>
                
                <DialogContent className={`${isMobile ? 'max-w-[95vw] h-[90vh] overflow-y-auto' : 'max-w-md'}`}>
                  <DialogHeader>
                    <DialogTitle>
                      {editingBloco ? 'Editar Bloco Horário' : 'Novo Bloco Horário'}
                    </DialogTitle>
                  </DialogHeader>
                  
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div>
                      <Label htmlFor="sala_id">Sala *</Label>
                      <Select 
                        value={watch('sala_id')} 
                        onValueChange={(value) => setValue('sala_id', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione uma sala" />
                        </SelectTrigger>
                        <SelectContent>
                          {salas.map((sala) => (
                            <SelectItem key={sala.id} value={sala.id}>
                              {sala.nome}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.sala_id && (
                        <p className="text-sm text-red-600 mt-1">{errors.sala_id.message}</p>
                      )}
                    </div>

                    <div className={`grid ${isMobile ? 'grid-cols-1 gap-4' : 'grid-cols-2 gap-4'}`}>
                      <div>
                        <Label htmlFor="hora_inicio">Hora Início *</Label>
                        <TouchInput
                          label="Hora Início"
                          id="hora_inicio"
                          type="time"
                          {...register('hora_inicio')}
                        />
                        {errors.hora_inicio && (
                          <p className="text-sm text-red-600 mt-1">{errors.hora_inicio.message}</p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="hora_fim">Hora Fim *</Label>
                        <TouchInput
                          label="Hora Fim"
                          id="hora_fim"
                          type="time"
                          {...register('hora_fim')}
                        />
                        {errors.hora_fim && (
                          <p className="text-sm text-red-600 mt-1">{errors.hora_fim.message}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="valor">Valor (R$) *</Label>
                      <TouchInput
                        label="Valor"
                        id="valor"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="180.00"
                        {...register('valor', { valueAsNumber: true })}
                      />
                      {errors.valor && (
                        <p className="text-sm text-red-600 mt-1">{errors.valor.message}</p>
                      )}
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch
                        id="ativo"
                        {...register('ativo')}
                        checked={watch('ativo')}
                        onCheckedChange={(checked) => setValue('ativo', checked)}
                      />
                      <Label htmlFor="ativo">Bloco ativo</Label>
                    </div>

                    <div className={`flex ${isMobile ? 'flex-col gap-2' : 'justify-end space-x-2'} pt-4`}>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setIsDialogOpen(false);
                          resetForm();
                        }}
                        className={isMobile ? 'w-full' : ''}
                      >
                        Cancelar
                      </Button>
                      <Button 
                        type="submit" 
                        disabled={isSubmitting}
                        className={isMobile ? 'w-full' : ''}
                      >
                        {isSubmitting ? 'Salvando...' : editingBloco ? 'Salvar' : 'Criar'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Time Blocks by Room */}
          <div className="space-y-6">
            {Object.keys(groupedBlocos).length === 0 ? (
              <Card>
                <CardContent className="text-center py-12">
                  <Clock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Nenhum bloco horário encontrado
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {searchTerm ? 'Tente ajustar os filtros de busca.' : 'Comece criando seu primeiro bloco horário.'}
                  </p>
                  {!searchTerm && (
                    <Button onClick={() => setIsDialogOpen(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Criar Primeiro Bloco
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              Object.entries(groupedBlocos).map(([salaNome, blocosSala]) => (
                <Card key={salaNome}>
                  <CardHeader className={isMobile ? 'p-4' : ''}>
                    <CardTitle className={`flex items-center gap-2 ${isMobile ? 'text-base' : ''}`}>
                      <Building className="w-5 h-5" />
                      {salaNome}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className={isMobile ? 'p-4 pt-0' : ''}>
                    <ResponsiveTable
                      data={blocosSala.sort((a, b) => {
                        const horaA = (a.hora_inicio ?? '').toString();
                        const horaB = (b.hora_inicio ?? '').toString();
                        return horaA < horaB ? -1 : horaA > horaB ? 1 : 0;
                      })}
                      columns={columns}
                      renderMobileCard={renderBlocoMobileCard}
                      loading={false}
                      emptyMessage="Nenhum bloco horário encontrado para esta sala."
                    />
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </>
      )}
    </ResponsiveAdminLayout>
  );
};

export default AdminTimeBlocks; 
